import { useEffect, useState } from 'react';
import type { EdgeMetrics, MetricResult } from '@/domain/metrics/contracts';
import { useEditorStore } from '../state/editor-store';
import { formatRatioAsPercent, formatUnknown } from './format';
import { localizeMetricWarning } from '@/app/localization';

/** SPEC-008 §17/§18/plan §13 — the edge properties panel's conversion field. */
export function EdgeConversionField({ edgeId, result }: { edgeId: string; result: MetricResult }) {
  const raw = useEditorStore(
    (state) => (state.graph?.edges.find((e) => e.id === edgeId)?.data?.metrics as EdgeMetrics | undefined)?.conversion,
  );
  const updateMetric = useEditorStore((state) => state.updateEdgeMetric);
  const isManual = raw?.mode === 'manual';
  const [draft, setDraft] = useState('');

  useEffect(() => {
    const manualValue = isManual && typeof raw?.manualValue === 'number' ? raw.manualValue : null;
    setDraft(manualValue === null ? '' : String(manualValue * 100));
  }, [isManual, raw?.manualValue]);

  const commit = () => {
    const parsed = Number(draft.replace(',', '.'));
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      updateMetric(edgeId, 'conversion', { mode: 'manual', manualValue: null });
      return;
    }
    updateMetric(edgeId, 'conversion', { mode: 'manual', manualValue: Math.min(1, Math.max(0, parsed / 100)) });
  };

  const explanationTitle = result.explanation
    ? `${result.explanation.formula}${result.calculatedCandidate !== null ? ` = ${formatRatioAsPercent(result.calculatedCandidate)}` : ''}`
    : undefined;

  return (
    <div className="space-y-1 border-t border-border-subtle pt-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-text-muted">Conversão</span>
        {result.origin ? (
          <span
            className="rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide"
            style={{
              color: result.origin === 'manual' ? 'var(--data-manual)' : 'var(--data-calculated)',
              border: `1px solid ${result.origin === 'manual' ? 'var(--data-manual)' : 'var(--data-calculated)'}`,
            }}
          >
            {result.origin === 'manual' ? 'Manual' : 'Calculada'}
          </span>
        ) : null}
      </div>
      {isManual ? (
        <input
          aria-label="Conversão da conexão"
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          className="w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        />
      ) : (
        <div
          className="rounded-md border border-dashed border-border bg-surface-2 px-2 py-1.5 text-xs text-text-secondary"
          title={explanationTitle}
        >
          {result.status === 'known' ? formatRatioAsPercent(result.effectiveValue as number | null) : formatUnknown()}
        </div>
      )}
      <button
        type="button"
        className="text-[10px] text-text-muted underline decoration-dotted underline-offset-2 hover:text-text-primary"
        onClick={() =>
          updateMetric(
            edgeId,
            'conversion',
            isManual ? { mode: 'auto', manualValue: null } : { mode: 'manual', manualValue: result.effectiveValue },
          )
        }
      >
        {isManual ? 'Usar calculada' : 'Substituir manualmente'}
      </button>
      {result.warnings.length > 0 ? (
        <ul className="space-y-0.5">
          {result.warnings.map((warning, index) => (
            <li key={index} className="text-[10px] text-[#eaa52d]">
              {localizeMetricWarning(warning)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
